<template>
  <div class="tw-mt-4">
    <div class="tw-flex tw-flex-col tw-mb-2">
      <WdsHeading type="title2">
        <div class="tw-flex tw-row tw-items-center tw-gap-2">
          {{ $t('Candidates') }}
          <FeatureFlagged name="job-candidates-v-1-2">
            <template #is-disabled>
              <WdsLabel xs warning subtle>
                Beta
                <WdsIcon
                  :size="13"
                  weight="thin"
                  :name="WdsIconNameEnum.LIGHT"
                  class="!tw-inline-block"
                />
              </WdsLabel>
            </template>
          </FeatureFlagged>
        </div>
      </WdsHeading>
    </div>
    <div class="card card-white candidates-table">
      <BaseDataTable
        :columns="columns"
        :entries="entries"
        :search-placeholder="$t('Search by name')"
        :loading="loadingCandidates"
        :search-disabled="true"
        @row-click="handleClickRow"
      >
        <template #additional-control-bar-elements>
          <div class="tw-flex tw-gap-sm tw-items-end">
            <div class="tw-w-[200px]">
              <WdsSelect
                v-model="candidateStep"
                :label="$t('Stage')"
                :list="JobCandidateStepListArray"
                :placeholder="$t('Select a stage')"
                multi-select
                data-test="candidates-filter-stage"
              >
                <template #option="{ option }">
                  <div :data-test="`candidates-filter-stage-${option.label.toLowerCase()}`">
                    {{ option.label }}
                  </div>
                </template>
              </WdsSelect>
            </div>

            <div class="tw-w-[200px]">
              <WdsSelect
                v-model="candidateStatus"
                :label="$t('Status')"
                :list="JobCandidateStatusListArray"
                :placeholder="$t('Select a status')"
                multi-select
                data-test="candidates-filter-status"
              >
                <template #option="{ option }">
                  <div :data-test="`candidates-filter-status-${option.label.toLowerCase()}`">
                    {{ option.label }}
                  </div>
                </template>
              </WdsSelect>
            </div>
            <WdsButtonGhost
              v-if="candidateStatus.length > 0 || candidateStep.length > 0"
              secondary
              class="!tw-mb-1"
              @click="handleResetFilters"
            >
              {{ $t('Reset filters') }}
              <WdsIcon class="tw-ml-2" name="reset" :size="18" />
            </WdsButtonGhost>
          </div>
        </template>
        <template #additional-control-bar-elements-right>
          <div class="tw-ml-2">
            <WdsInput
              prefix-icon="search"
              :placeholder="$t('Search name')"
              data-test="candidates-search-input"
              @input="handleSearch"
            />
          </div>
        </template>
        <template #no-results="{ loading }">
          <WdsStack direction-row justify-center data-test="no-candidates-found">
            <BaseLoadingNotice v-if="loading" class="tw-z-10" data-test="candidates-loading">
              <template #main>{{ $t('Loading candidates') }}</template>
            </BaseLoadingNotice>
            <template v-else>
              <div
                class="tw-flex tw-flex-col tw-gap-6 tw-w-15 tw-py-6"
                data-test="no-candidates-found"
              >
                <div class="tw-flex tw-flex-col tw-gap-2">
                  <WdsHeading type="title3">{{ $t('Add talent to this job') }}</WdsHeading>

                  <WdsText
                    :sub-text="
                      $t(
                        'Build a private shortlist of workers to review with your team before inviting them, or invite contacts to see this job and apply straight away',
                      )
                    "
                  />
                </div>

                <template v-if="jobObj.published">
                  <div class="tw-flex tw-flex-row tw-justify-center tw-gap-2">
                    <WdsButton
                      :disabled="!jobObj.viewerCanCreateJobCandidate"
                      secondary
                      data-test="candidates-shortlist"
                      @click="toggleShortlistingContactsSelectionModal(true)"
                    >
                      {{ $t('Shortlist workers') }}
                    </WdsButton>

                    <WdsButton
                      v-if="jobObj.available"
                      :disabled="!jobObj.viewerCanShare"
                      primary
                      data-test="candidates-invite-to-apply"
                      @click="toggleInviteToApplyContactsSelectionModal(true)"
                    >
                      {{ $t('Invite to apply') }}
                    </WdsButton>
                  </div>

                  <WdsAlert success class="tw-border tw-border-green-200">
                    {{
                      $t('Shortlisted workers are added privately to this job and are not notified')
                    }}
                  </WdsAlert>
                </template>
              </div>
            </template>
          </WdsStack>
        </template>
        <template #Head-Preferred><WdsIcon name="star-filled" :size="16" /></template>
        <template
          #Preferred="{ row, rowIndex }: { row: JobCandidateFragmentType; rowIndex: number }"
        >
          <div v-if="row.viewerCanUpdatePreferred">
            <span
              :data-test="`candidate-preferred-${rowIndex}-${
                row.isPreferred ? 'star-filled' : 'star'
              }`"
              @click="handlePreferred(row.id, !row.isPreferred)"
            >
              <WdsSpinner v-if="preferredLoadingIds.includes(row.id)" indeterminate />
              <WdsIcon
                v-else
                :name="row.isPreferred ? 'star-filled' : 'star'"
                :size="16"
                @click="handlePreferred(row.id, !row.isPreferred)"
              />
            </span>
          </div>
        </template>
        <template
          #Candidate="{ row, rowIndex }: { row: JobCandidateFragmentType; rowIndex: number }"
        >
          <WorkerName
            :manages-workers="row.worker.hiresWithAttribution[0]?.recruiterManagesWorkers ?? false"
            :recruiter-name="row.worker.hiresWithAttribution[0]?.recruiter?.name"
            :recruiter-fee-percentage="row.worker.hiresWithAttribution[0]?.recruiterFee"
            :recruiter-ownership-start-date="
              row.worker.hiresWithAttribution[0]?.recruiterOwnershipStartDate
            "
            :recruiter-ownership-days="row.worker.hiresWithAttribution[0]?.recruiterOwnershipDays"
            :recruiter-ownership-days-left="
              row.worker.hiresWithAttribution[0]?.recruiterOwnershipDaysLeft
            "
          >
            <template #name>
              <WdsText
                :sub-text="row.worker.jobTitle ?? ''"
                :data-test="`candidate-name-${rowIndex}`"
              >
                {{ row.worker.name }}
              </WdsText>
            </template>
          </WorkerName>
        </template>
        <template #Stage="{ row, rowIndex }: { row: JobCandidateFragmentType; rowIndex: number }">
          <WdsLabel
            v-if="row.step"
            :primary="
              row.step === JobCandidateHiringStep.InvitedToApply ||
              row.step === JobCandidateHiringStep.Applied
            "
            :success="row.step === JobCandidateHiringStep.Hiring"
            :subtle="
              row.step === JobCandidateHiringStep.InvitedToApply ||
              row.step === JobCandidateHiringStep.Hiring ||
              row.step === JobCandidateHiringStep.Shortlisted
            "
            :gray="row.step === JobCandidateHiringStep.Shortlisted"
            :data-test="`candidate-stage-${rowIndex}`"
          >
            {{ JobCandidateStepList[row.step].label }}
          </WdsLabel>
        </template>
        <template #Status="{ row, rowIndex }: { row: JobCandidateFragmentType; rowIndex: number }">
          <WdsStack gap-sm align-center>
            <WdsLabel
              v-if="row.status"
              :success="row.status === JobCandidateStatus.Eligible"
              :error="row.status === JobCandidateStatus.NonEligible"
              :subtle="true"
              :data-test="`candidate-status-${rowIndex}`"
            >
              {{ JobCandidateStatusList[row.status].label }}
            </WdsLabel>
            <WdsTooltip v-if="row.statusReason" :data-test="`candidate-status-${rowIndex}-tooltip`">
              <WdsIcon name="help-filled" :size="20" />
              <template #tooltip-content>
                <span :data-test="`candidate-status-${rowIndex}-tooltip-reason`">
                  {{ JobCandidateStatusReasonList[row.statusReason]?.label }}
                </span>
                <div
                  v-if="row.statusComment"
                  :data-test="`candidate-status-${rowIndex}-tooltip-comment`"
                >
                  {{ row.statusComment }}
                </div>
              </template>
            </WdsTooltip>
          </WdsStack>
        </template>
        <template #Channel="{ row }: { row: JobCandidateFragmentType; rowIndex: number }">
          <WdsTag
            v-if="row.sourcingChannel"
            :success="row.sourcingChannel === SourcingChannel.TrustedContact"
            :gray="row.sourcingChannel === SourcingChannel.CandidateSubmission"
            :primary="row.sourcingChannel === SourcingChannel.Marketplace"
          >
            <template
              v-if="row.sourcingChannel === SourcingChannel.CandidateSubmission && row.owner"
            >
              {{ row.owner.name }}
            </template>
            <template v-else>
              {{ SourcingChannelList[row.sourcingChannel].label }}
            </template>
          </WdsTag>
        </template>
        <template #Action="{ row, rowIndex }: { row: JobCandidate; rowIndex: number }">
          <CandidateActionMenu
            :job="job"
            :job-candidate="row"
            :data-test="`candidate-menu-${rowIndex}`"
            @candidate-updated="refetch"
            @show-drawer="handleShowDetailPanel"
          >
            <template #action>
              <WdsMenuOption
                v-if="shouldShowDetailPanel(row) !== undefined"
                data-test="candidate-menu-profile"
              >
                <a
                  v-if="!shouldShowDetailPanel(row)"
                  :href="row.worker?.profile?.url"
                  data-test="candidate-menu-profile-redirect"
                >
                  {{ $t('Go to profile') }}
                </a>
                <div
                  v-else
                  data-test="candidate-menu-profile-view"
                  @click="handleShowDetailPanel(row.worker.id)"
                >
                  {{ $t('View profile') }}
                </div>
              </WdsMenuOption>
            </template>
          </CandidateActionMenu>
        </template>
      </BaseDataTable>

      <div v-if="entries.length > 0" class="tw-mt-10 tw-py-4 tw-px-4 card">
        <div class="tw-flex tw-flex-row tw-justify-between tw-items-center">
          <div class="tw-flex tw-flex-col">
            <WdsHeading type="title4">Add talent</WdsHeading>
            <WdsText
              :sub-text="
                $t('Shortlisted workers are added privately to this job and are not notified')
              "
            />
          </div>

          <div class="tw-flex tw-flex-row tw-gap-2">
            <div>
              <WdsButton
                :disabled="!jobObj.viewerCanCreateJobCandidate"
                secondary
                data-test="candidates-shortlist"
                @click="toggleShortlistingContactsSelectionModal(true)"
              >
                {{ $t('Shortlist workers') }}
              </WdsButton>
            </div>

            <div v-if="jobObj.available">
              <WdsButton
                :disabled="!jobObj.viewerCanShare"
                primary
                data-test="candidates-invite-to-apply"
                @click="toggleInviteToApplyContactsSelectionModal(true)"
              >
                {{ $t('Invite to apply') }}
              </WdsButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <WdsModal
      lg
      :show="showShortlistingContactsSelectionModal"
      :title="$t('Shortlist workers')"
      data-test="candidates-shortlist-modal"
      @cancel="toggleShortlistingContactsSelectionModal(false)"
    >
      <SelectShortlistingCandidatesModal
        :company-id="jobObj.company.id"
        :job-id="jobObj.id"
        :pre-selected-candidates="preSelectedShortlistingCandidates"
        @candidates-updated="handleContactsUpdated"
        @cancel="toggleShortlistingContactsSelectionModal(false)"
      />
    </WdsModal>

    <WdsModal
      lg
      :show="showInviteToApplyContactsSelectionModal"
      :title="$t('Invite to apply')"
      data-test="candidates-invite-to-apply-modal"
      @cancel="toggleInviteToApplyContactsSelectionModal(false)"
    >
      <SelectInviteToApplyCandidatesModal
        :company-id="jobObj.company.id"
        :job-id="jobObj.id"
        :pre-selected-candidates="preSelectedInvitedToApplyCandidates"
        @candidates-updated="handleContactsUpdated"
        @cancel="toggleInviteToApplyContactsSelectionModal(false)"
      />
    </WdsModal>
  </div>
  <WdsDrawer
    v-if="selectedWorkerId !== undefined"
    v-model:show="showDetailPanel"
    position="right"
    width="420px"
    padding="0"
  >
    <WorkerDetailPanel
      :worker-id="selectedWorkerId"
      :viewer-account-id="jobObj.company.id"
      :readonly="true"
      @close="handleResetDetailPanel"
      @updated="refetch"
      @deleted="refetch"
    />
  </WdsDrawer>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import moment from 'moment'
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsDrawer,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsLabel,
  WdsMenuOption,
  WdsModal,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'

import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

import { GetJobCandidatesQuery } from '@/components/Job/Candidates/graphql/queries'
import { UpdateJobCandidatePreferredMutation } from '@/components/Job/Candidates/graphql/mutations'

import {
  JobCandidate,
  JobCandidateFragment as JobCandidateFragmentType,
  JobCandidateHiringStep,
  JobCandidateStatus,
  SourcingChannel,
} from '@/gql/graphql'
import {
  JobCandidateStatusList,
  JobCandidateStatusListArray,
  JobCandidateStatusReasonList,
  JobCandidateStepList,
  JobCandidateStepListArray,
  SourcingChannelList,
} from '@/components/Job/constants'
import { debounce } from '@/utils/tools'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import useApolloError from '@/composables/useApolloError'
import NetworkError from '@/utils/notification/NetworkError'
import CandidateActionMenu from '@/components/Job/Candidates/CandidateActionMenu.vue'
import SelectShortlistingCandidatesModal from '@/components/Job/Candidates/SelectShortlistingCandidatesModal.vue'
import SelectInviteToApplyCandidatesModal from '@/components/Job/Candidates/SelectInviteToApplyCandidatesModal.vue'
import WorkerName from '@/components/WorkerName.vue'
import { JobCandidateFragment, JobFragment } from '@/components/Job/graphql/fragments'
import { type FragmentType, useFragment } from '@/gql'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import { GetWorkerTrustedContactsQuery } from '@/components/Contacts/CompanyFreelancer/graphql/queries'
import { TrustedContactLookupFragment } from '@/components/Contacts/CompanyFreelancer/graphql/fragments'

const props = defineProps<{
  job: FragmentType<typeof JobFragment>
}>()

const jobObj = useFragment(JobFragment, props.job)

const candidateStatus = ref<{ label: string; value: JobCandidateStatus }[]>([])
const candidateStep = ref<{ label: string; value: JobCandidateHiringStep }[]>([])

const searchValue = ref<string>('')
const showShortlistingContactsSelectionModal = ref<boolean>(false)
const showInviteToApplyContactsSelectionModal = ref<boolean>(false)

const selectedWorkerId = ref<string | undefined>(undefined)
const selectedTrustedContactId = ref<string | undefined>(undefined)
const showDetailPanel = ref(false)

const handleResetFilters = () => {
  candidateStatus.value = []
  candidateStep.value = []
}

const handleSearch = debounce((event) => {
  searchValue.value = event.target.value
}, 250)

const { mutate: setPreferredMutation, onError: preferredOnError } = useMutation(
  UpdateJobCandidatePreferredMutation,
  {
    clientId: 'publicApi',
  },
)

const preferredLoadingIds = ref<string[]>([])

const handlePreferred = async (id, preferred) => {
  const mutation = setPreferredMutation({
    input: {
      jobCandidate: id,
      isPreferred: preferred,
    },
  })

  preferredLoadingIds.value.push(id)

  await mutation

  preferredLoadingIds.value.splice(preferredLoadingIds.value.indexOf(id), 1)
}

preferredOnError((error) => {
  const { validationErrors } = useApolloError(error)

  const validationErrorKeys = Object.keys(validationErrors.value)

  if (validationErrorKeys.length > 0) {
    validationErrorKeys.forEach((key) => {
      new NetworkError({
        msg: validationErrors.value[key],
      }).dispatch()
    })
  } else if (error.networkError) {
    new NetworkError({
      msg: error.networkError.message,
    }).dispatch()
  } else {
    new NetworkError({
      msg: error.message,
    }).dispatch()
  }
})

const columns = [
  {
    key: 'preferred',
    slot: 'Preferred',
    ignoreClickForRowSelection: true,
  },
  {
    key: 'candidate',
    slot: 'Candidate',
    title: $t('Candidate'),
    sort: 'worker.name',
    order: 'asc',
    orderAble: true,
  },
  {
    key: 'step',
    slot: 'Stage',
    title: $t('Stage'),
    orderAble: true,
  },
  {
    key: 'status',
    slot: 'Status',
    title: $t('Status'),
    searchKeys: [''],
  },
  {
    key: 'channel',
    slot: 'Channel',
    title: $t('Channel'),
    searchKeys: [''],
  },
  {
    key: 'createdAt',
    slot: 'DateAdded',
    title: $t('Date added'),
    searchKeys: [''],
  },
  {
    key: 'updatedAt',
    slot: 'UpdatedAt',
    title: $t('Last Updated'),
    searchKeys: [''],
  },
  {
    key: 'action',
    slot: 'Action',
    searchKeys: [''],
    colClass: 'tw-justify-right',
    ignoreClickForRowSelection: true,
  },
]

const {
  result,
  loading: loadingCandidates,
  refetch,
} = useQuery(
  GetJobCandidatesQuery,
  {
    jobIds: [jobObj.id],
    first: PER_PAGE_MAXIMUM,
  },
  { clientId: 'publicApi' },
)

const filterStatuses = (row) =>
  candidateStatus?.value?.length
    ? candidateStatus.value.map(({ value }) => value).includes(row.status)
    : row

const filterSteps = (row) =>
  candidateStep?.value?.length
    ? candidateStep.value.map(({ value }) => value).includes(row.step)
    : row

const searchName = (row) => row.worker.name.toLowerCase().includes(searchValue.value.toLowerCase())

const jobCandidates = computed(() =>
  result.value?.jobCandidates.data.map((entry: FragmentType<typeof JobCandidateFragment>) =>
    useFragment(JobCandidateFragment, entry),
  ),
)

const entries = computed(
  () =>
    jobCandidates.value
      ?.map((jobCandidate) => ({
        ...jobCandidate,
        createdAt: moment(jobCandidate.createdAt).format('YYYY-MM-DD'),
        updatedAt: moment(jobCandidate.updatedAt).fromNow(),
      }))
      .filter(filterStatuses)
      .filter(filterSteps)
      .filter(searchName) ?? [],
)

const trustedContactsVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  workers: jobCandidates.value?.map((jobCandidate) => jobCandidate.worker?.id) ?? [],
  first: jobCandidates.value?.length ?? 0,
}))

const trustedContactEnabled = computed(() => (jobCandidates.value?.length ?? 0) > 0)
const { result: resultTrustedContacts } = useQuery(
  GetWorkerTrustedContactsQuery,
  trustedContactsVariables,
  {
    clientId: 'publicApi',
    enabled: trustedContactEnabled,
  },
)

const trustedContacts = computed(() =>
  resultTrustedContacts.value?.trustedContacts.data.map(
    (entry: FragmentType<typeof TrustedContactLookupFragment>) =>
      useFragment(TrustedContactLookupFragment, entry),
  ),
)

const preSelectedShortlistingCandidates = computed(
  () => jobCandidates.value?.map((jobCandidate) => jobCandidate.worker.id) ?? [],
)

const preSelectedInvitedToApplyCandidates = computed(
  () =>
    jobCandidates.value
      ?.filter((jobCandidate) => jobCandidate.jobShare?.isActive)
      .map((jobCandidate) => jobCandidate.worker.id) ?? [],
)

const toggleShortlistingContactsSelectionModal = (show: boolean) => {
  showShortlistingContactsSelectionModal.value = show
}

const toggleInviteToApplyContactsSelectionModal = (show: boolean) => {
  showInviteToApplyContactsSelectionModal.value = show
}

const getTrustedContactId = (workerId: string) =>
  trustedContacts.value?.filter((trustedContact) => trustedContact?.worker.id === workerId)[0]
    ?.id ?? undefined

const shouldShowDetailPanel = (row: JobCandidateFragmentType) => {
  if (trustedContactEnabled.value && getTrustedContactId(row.worker.id)) {
    return true
  }

  if (row.worker?.profile?.url) {
    return false
  }

  return undefined
}

const handleResetDetailPanel = () => {
  selectedWorkerId.value = undefined
  selectedTrustedContactId.value = undefined
  showDetailPanel.value = false
}

const handleShowDetailPanel = (workerId) => {
  selectedWorkerId.value = workerId
  selectedTrustedContactId.value = getTrustedContactId(workerId)
  showDetailPanel.value = true
}

const handleClickRow = (row) => {
  if (shouldShowDetailPanel(row)) {
    handleShowDetailPanel(row.worker.id)
  }
}

const handleContactsUpdated = async () => {
  refetch()
}
</script>

<style scoped lang="scss">
:deep(.wds-modal) {
  z-index: 19;
}

.candidates-table {
  :deep(.data-table-head) {
    @apply tw-items-end;
  }
  :deep(.wds-menu-option-content) {
    @apply tw-w-max;
  }
  :deep(.table-wrap tr:not(.table-empty)) {
    @apply tw-cursor-pointer;
  }
  :deep(.data-table-head) {
    @apply tw-px-1;
  }
  :deep(.wds-input-label) {
    @apply tw-mb-0;
  }
  :deep(select.data-table-head-input) {
    @apply tw-h-7.5;
  }
}
</style>
